import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CrackCUETSection = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 my-10">
    <h2 className="text-xl font-bold text-blue-900 mb-3">How Crack CUET Helps Get Better CUET Percentiles</h2>
    <p className="text-gray-700 leading-relaxed">
      Crack CUET is India's most focused CUET mock test platform, built by toppers from SRCC, St. Stephen's, and Hindu College. Every mock test is designed to perfectly mirror the CUET exam syllabus — section-wise, topic-wise, and difficulty-level-wise. Our AI generates fresh questions from every chapter of the NCERT syllabus so your preparation is comprehensive, not patchy. With detailed post-test analytics showing topic-wise accuracy, you'll know exactly which chapters in your syllabus need more attention. Thousands of CUET aspirants use Crack CUET to convert their NCERT revision into exam-ready performance — consistently reaching the 95th percentile and above.
    </p>
    <p className="mt-5 text-center">
      <Link to="/dashboard" className="inline-block bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors">
        Start Practising Free — 1 Full Mock Included
      </Link>
    </p>
  </div>
);

const SubjectRow = ({ subject, weightage, topics }) => (
  <div className="border border-gray-200 rounded-lg p-4 mb-3">
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-semibold text-gray-900">{subject}</h4>
      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{weightage}</span>
    </div>
    <p className="text-sm text-gray-600">{topics}</p>
  </div>
);

export default function BlogCUETSyllabus2026() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>CUET Syllabus 2026 — Complete Subject-Wise Breakdown | Crack CUET</title>
        <meta name="description" content="Complete CUET 2026 syllabus broken down subject-wise — Physics, Chemistry, Maths, Biology, Economics, History, and all 15 subjects with important topics." />
        <meta name="keywords" content="CUET syllabus 2026, CUET subject wise syllabus, CUET Physics syllabus, CUET Chemistry syllabus, NTA CUET syllabus" />
        <link rel="canonical" href="https://crackcuet.com/blog/cuet-syllabus-2026-complete-subject-wise-breakdown" />
        <meta property="og:title" content="CUET Syllabus 2026 — Complete Subject-Wise Breakdown" />
        <meta property="og:description" content="All 15 CUET subjects explained with important topics, weightage, and preparation tips." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://crackcuet.com/blog/cuet-syllabus-2026-complete-subject-wise-breakdown" />
      </Helmet>
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center"><img src="/logo-full.png" alt="Crack CUET" className="h-16" /></Link>
          <Link to="/blog" className="text-sm text-gray-500 hover:text-gray-900">All Blogs</Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 py-12">
        <div className="mb-8">
          <span className="text-sm font-medium text-blue-600">Syllabus</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2 mb-4 leading-tight">
            CUET Syllabus 2026 — Complete Subject-wise Breakdown &amp; High-Weightage Topics
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>Feb 28, 2026</span>
            <span>14 min read</span>
            <span>By Crack CUET Team</span>
          </div>
        </div>

        <img
          src="https://images.unsplash.com/photo-1646495785840-7ff6be438d63?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
          alt="CUET syllabus 2026 complete subject guide open book"
          className="w-full rounded-xl mb-8 object-cover max-h-80"
        />

        <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed space-y-6">
          <p>
            The <strong>CUET syllabus 2026</strong> is directly based on the <strong>NCERT Class 12 curriculum</strong> for domain subjects. This is both great news and a trap. Great news because you've (presumably) already studied the syllabus in school. A trap because students underestimate how specifically and rigorously CUET questions test these concepts — far more precisely than your board exam would.
          </p>
          <p>
            This guide breaks down the syllabus for all three sections of CUET — Section I (Languages), Section II (Domain Subjects), and Section III (General Test) — with high-weightage topics called out for each subject.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-6">
            <p className="text-amber-800 font-semibold mb-1">Important Note</p>
            <p className="text-amber-700 text-sm">CUET domain subject papers are based on the <strong>NCERT Class 12 syllabus only</strong>. Class 11 content is NOT tested in domain subjects (except in a few universities that specify otherwise). However, the General Test (Section III) requires current affairs knowledge beyond Class 12 textbooks.</p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Section I — Languages (IA and IB)</h2>
          <p>The language section tests:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Reading comprehension</strong> — formal passages, literary texts, news-style writing</li>
            <li><strong>Verbal ability</strong> — vocabulary in context, synonyms, antonyms</li>
            <li><strong>Rearrangement</strong> — sentence ordering and paragraph coherence</li>
            <li><strong>Literary aptitude</strong> — for literature-specific language papers</li>
          </ul>
          <p>
            <strong>English (most popular):</strong> Focus on formal reading comprehension (business, academic, science-related passages). Avoid spending too much time on literary/poetry sections if you're not taking an English literature course.
          </p>
          <p>
            <strong>Hindi:</strong> Passages from Hindi prose, essay comprehension, and unseen poetry questions are common. Ensure you can interpret formal Hindi — not just conversational.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Section II — Domain Subjects</h2>
          <p>Each domain paper covers the <strong>complete Class 12 NCERT syllabus</strong> for that subject. Here's a quick breakdown of high-weightage areas for the most popular subjects:</p>

          <SubjectRow
            subject="Economics (Class 12)"
            weightage="Most popular domain"
            topics="Macroeconomics (National Income, Money & Banking, Govt Budget, Balance of Payments) carries ~60% weight. Microeconomics (Theory of Demand/Supply, Market Forms, Consumer Behaviour) forms the rest. Numericals on NI accounting and money multiplier are high-frequency."
          />
          <SubjectRow
            subject="Business Studies (Class 12)"
            weightage="Commerce stream"
            topics="Management (Nature & Significance, Planning, Organising, Directing, Controlling) — each chapter has high MCQ frequency. Marketing Management and Consumer Protection are lighter but commonly tested. Financial Management chapter is very formula-heavy."
          />
          <SubjectRow
            subject="Accountancy (Class 12)"
            weightage="Commerce stream"
            topics="Partnership Accounts (Admission, Retirement, Death of Partner, Dissolution) typically forms 50%+ of the paper. Company Accounts (Issue of Shares, Debentures) and Financial Statement Analysis are also high-priority. Strong numerical practice is essential."
          />
          <SubjectRow
            subject="History (Class 12)"
            weightage="Humanities stream"
            topics="Bricks, Beads & Bones (Indus Valley) | Kings, Farmers & Towns | Kinship, Caste & Class | Thinkers & Traditions | Through the Eyes of Travellers | Bhakti-Sufi Traditions | Rebels & Raj (1857) | Colonial Cities | Mahatma Gandhi & National Movement | Partition | Constitution Making. 12 themes, each equally likely."
          />
          <SubjectRow
            subject="Political Science (Class 12)"
            weightage="Humanities stream"
            topics="Both books: Contemporary World Politics (Cold War, Non-alignment, South Asia, UN, etc.) and Politics in India since Independence (Nation Building, Party System, Coalitions, Emergency, Regional Movements). Current affairs linkages are common in MCQs."
          />
          <SubjectRow
            subject="Biology (Class 12)"
            weightage="Science stream"
            topics="Reproduction (Sexual in Flowering Plants, Human Reproduction, Reproductive Health), Genetics & Evolution (Mendelian Inheritance, Molecular Basis, Evolution), Biology in Human Welfare (Human Health, Microbes), Biotechnology (Principles, Applications), Ecology (Organisms, Population, Ecosystem, Biodiversity). Diagram-based MCQs are very common."
          />
          <SubjectRow
            subject="Chemistry (Class 12)"
            weightage="Science stream"
            topics="Solutions, Electrochemistry, Chemical Kinetics, and d-f Block Elements are very high frequency. Coordination Compounds, Aldehydes/Ketones/Acids, Biomolecules, and Polymers are also commonly tested. Numericals on electrochemistry and kinetics appear in almost every mock."
          />
          <SubjectRow
            subject="Physics (Class 12)"
            weightage="Science stream"
            topics="Electrostatics and Current Electricity together form ~35% of the paper. Magnetism, EMI & AC, Optics (Ray + Wave), and Modern Physics (Atoms, Nuclei, Semiconductors) are essential. Numerical solving speed is the key differentiator in Physics."
          />
          <SubjectRow
            subject="Mathematics (Class 12)"
            weightage="Science/Commerce streams"
            topics="Relations & Functions, Inverse Trig, Matrices & Determinants, Calculus (Continuity, Differentiability, Integration, Differential Equations), Vectors & 3D Geometry, Linear Programming, and Probability. Calculus typically accounts for 40–50% of the paper."
          />
          <SubjectRow
            subject="Psychology (Class 12)"
            weightage="Humanities stream"
            topics="Variations in Psychological Attributes, Self & Personality, Human Strengths (Positive Psychology), Psychotherapies, Attitude & Social Cognition, Social Influence, Developing Psychological Skills. Very NCERT-bound; exact definitions and case-based questions are common."
          />
          <SubjectRow
            subject="Sociology (Class 12)"
            weightage="Humanities stream"
            topics="Indian Society (Structural Change, Cultural Change, Indian Democracy, Changes in Economy & Polity, Social Movements) and Social Change & Development in India. Conceptual clarity and ability to link concepts to current examples is key."
          />

          <img
            src="https://images.unsplash.com/photo-1663057571146-9e796b0efb60?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
            alt="CUET syllabus preparation detailed notes study"
            className="w-full rounded-xl my-8 object-cover max-h-72"
          />

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Section III — General Test (GAT)</h2>
          <p>
            The General Test has 75 questions (60 to attempt) in 60 minutes. The GAT syllabus is the broadest — it's NOT limited to any single textbook or class. Here's what it covers:
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6">General Mental Ability</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Series completion (number, letter, alphabetical)</li>
            <li>Analogies and Classification</li>
            <li>Coding-Decoding</li>
            <li>Blood relations and seating arrangements</li>
            <li>Direction-based reasoning</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6">Numerical Ability</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Percentage, Ratio &amp; Proportion, Profit &amp; Loss</li>
            <li>Time &amp; Work, Time &amp; Distance, Pipes &amp; Cisterns</li>
            <li>Simple &amp; Compound Interest</li>
            <li>Averages, Mixtures, Allegations</li>
            <li>Data Interpretation (tables, bar graphs, pie charts, line graphs)</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6">Quantitative Reasoning</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Set theory and Venn diagrams</li>
            <li>Permutation &amp; Combination basics</li>
            <li>Geometry fundamentals (area, perimeter, circles)</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6">General Knowledge &amp; Current Affairs</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>National and international current events (last 12 months)</li>
            <li>Important government schemes and programmes</li>
            <li>Constitution of India — basic provisions, fundamental rights</li>
            <li>Science and technology milestones</li>
            <li>Sports, awards, geography basics</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-10">How to Use This Syllabus Breakdown Effectively</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Audit your Class 12 NCERT coverage</strong> — go chapter by chapter and mark what you've fully studied, partially studied, and not touched</li>
            <li><strong>Prioritise high-weightage chapters</strong> — the ones marked above get you the most marks per hour of study</li>
            <li><strong>Use NCERT exemplar problems</strong> for Science and Maths — they are closer to CUET difficulty than standard NCERT exercises</li>
            <li><strong>Build a GAT routine</strong> — spend at least 45 minutes daily on aptitude and current affairs, separate from domain prep</li>
            <li><strong>Take subject-wise mock tests</strong> — after completing each subject's NCERT, take 2–3 mocks for that subject to identify gaps before moving on</li>
          </ol>

          <CrackCUETSection />

          <h2 className="text-2xl font-bold text-gray-900 mt-10">Final Thoughts</h2>
          <p>
            The CUET 2026 syllabus is wide but navigable. The students who struggle are those who try to study beyond NCERT for domain subjects (wasting time on coaching-level content) or who ignore the GAT section until the last month (when it's too late to build speed). Follow a NCERT-first, mock-test-verified approach — and you'll find yourself in a much stronger position than most competitors. Good luck.
          </p>
        </div>
      </article>
    </div>
  );
}
